import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'
import React, { useCallback } from 'react'
import { useNavigate } from 'react-router-dom-v5-compat'

import { urlForEntity } from './notificationCenterViews'
import { dayOfWeekOf } from './useOptimizationReview'

import type { SxProps, Theme } from '@mui/material/styles'
import type { TypographyProps } from '@mui/material/Typography'
import type { NotificationEntityType } from 'types/modules/notification-center/notification'

type NotificationEntityLinkProps = {
  label: string
  entityType?: NotificationEntityType
  entityId?: string
  // Route optimization only: reopens that run's comparison on the board.
  jobId?: string
  // The run's day. Without it the board opens on its own default, where the job's column is absent.
  serviceDate?: string
  variant?: TypographyProps['variant']
  /** Call sites close the weight gap here — the scale has no 12/400 or 14/500 step. */
  sx?: SxProps<Theme>
  onClick?: (event: React.MouseEvent) => void
}

/**
 * An entity reference, linked when it has somewhere to go. The `href` is real so the icon's promise
 * holds — modifier-click opens a tab — while a plain click routes in-app rather than reloading the SPA.
 * References with no destination yet render as text, so nothing looks clickable that isn't.
 */
const NotificationEntityLink = ({
  label,
  entityType,
  entityId,
  jobId,
  serviceDate,
  variant = 'body1',
  sx,
  onClick,
}: NotificationEntityLinkProps) => {
  const navigate = useNavigate()
  const href = urlForEntity(entityType, entityId, jobId, serviceDate ? dayOfWeekOf(serviceDate) : undefined)

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      onClick?.(event)
      if (!href || event.metaKey || event.ctrlKey || event.shiftKey) return
      event.preventDefault()
      navigate(href)
    },
    [href, navigate, onClick]
  )

  if (!href) {
    return (
      <Typography component="p" sx={sx} variant={variant}>
        {label}
      </Typography>
    )
  }

  return (
    <Link
      href={href}
      sx={[{ display: 'inline-flex', alignItems: 'center', gap: 0.5, alignSelf: 'flex-start' }, ...(Array.isArray(sx) ? sx : [sx])]}
      underline="hover"
      variant={variant}
      onClick={handleClick}
    >
      {label}
      <OpenInNewOutlinedIcon fontSize="inherit" sx={{ flexShrink: 0 }} />
    </Link>
  )
}

export default NotificationEntityLink
